import React, { useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Copy, Loader2, ShieldCheck, Trash2 } from "lucide-react";

/**
 * Trova gli interventi scritti due volte sulla stessa macchina.
 *
 * Due criteri, e sono diversi apposta.
 *
 * «Righe identiche»: fanno gruppo solo le righe uguali in TUTTO — data, tipo,
 * descrizione, fornitore, costo, prossima scadenza, note, spunte. È il caso
 * del file importato due volte, e togliere una copia non perde niente.
 *
 * «Stessa data e stesso importo»: stesso mezzo, stessa data, stessa cifra, e
 * non importa il resto. Qui le righe NON sono uguali — cambia la descrizione,
 * o il fornitore — quindi qualcosa si perde per forza. Si propone di tenere
 * quella con più campi compilati, e si mostra ogni copia per esteso: la
 * scelta la fa chi guarda, non il programma.
 *
 * In tutti e due i casi non si cancella niente d'ufficio: ogni copia ha la sua
 * spunta, e la riga che si tiene non è nemmeno spuntabile.
 */
const CRITERI = [
  { codice: "identiche", nome: "Righe identiche in tutto" },
  { codice: "data_importo", nome: "Stessa data e stesso importo" },
];

export default function DoppioniInterventi({ onDone }) {
  const [aperto, setAperto] = useState(false);
  const [dati, setDati] = useState(null);
  const [carico, setCarico] = useState(false);
  const [pulendo, setPulendo] = useState(false);
  const [scelte, setScelte] = useState({});      // id riga -> true/false
  const [aperti, setAperti] = useState({});      // indice gruppo -> aperto
  const [criterio, setCriterio] = useState("identiche");
  const [cerca, setCerca] = useState("");

  const carica = async (quale) => {
    setCarico(true);
    setDati(null);
    setAperti({});
    try {
      const { data } = await api.get(`/equipment/doppioni?criterio=${quale}`);
      setDati(data);
      // Di partenza si propone di togliere le copie in più, mai quella che si
      // tiene. Col criterio largo restano comunque tutte da guardare.
      const iniziali = {};
      (data.righe || []).forEach((g) => g.da_togliere.forEach((id) => { iniziali[id] = true; }));
      setScelte(iniziali);
    } catch {
      toast.error("Errore nel controllo");
      setAperto(false);
    } finally { setCarico(false); }
  };

  const guarda = async () => {
    setAperto(true);
    setCerca("");
    setCriterio("identiche");
    await carica("identiche");
  };

  const cambiaCriterio = async (quale) => {
    setCriterio(quale);
    await carica(quale);
  };

  const selezionate = useMemo(
    () => Object.entries(scelte).filter(([, v]) => v).map(([id]) => id),
    [scelte]);

  // Su un parco di ottanta macchine l'elenco è lungo: la casella filtra per
  // targa, marca o descrizione, così si arriva subito al mezzo che si cerca.
  const visibili = useMemo(() => {
    const q = cerca.trim().toLowerCase();
    if (!q) return dati?.righe || [];
    return (dati?.righe || []).filter((g) => [
      g.mezzo, g.identifier, g.categoria, g.descrizione, g.fornitore, g.data,
    ].some((x) => String(x || "").toLowerCase().includes(q)));
  }, [dati, cerca]);

  const cambiaGruppo = (gruppo, valore) =>
    setScelte((s) => {
      const n = { ...s };
      gruppo.da_togliere.forEach((id) => { n[id] = valore; });
      return n;
    });

  // «Spunta tutto» lavora su quello che si sta guardando, non su tutto il
  // parco: se hai filtrato per una targa, si spunta quella e basta.
  const tutte = (valore) =>
    setScelte((s) => {
      const n = { ...s };
      visibili.forEach((g) => g.da_togliere.forEach((id) => { n[id] = valore; }));
      return n;
    });

  const pulisci = async () => {
    if (!window.confirm(
      `Togliere ${selezionate.length} righe?\n\n` +
      "Restano tutte le altre, comprese le prime copie di ogni gruppo. " +
      "L'operazione non si annulla."
    )) return;
    setPulendo(true);
    try {
      const { data } = await api.post("/equipment/doppioni/pulisci",
                                      { ids: selezionate, criterio });
      toast.success(`${data.tolte} righe tolte su ${data.mezzi} mezzi`);
      setAperto(false);
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella pulizia");
    } finally { setPulendo(false); }
  };

  return (
    <>
      <Button variant="outline" className="h-12" onClick={guarda}
              data-testid="doppioni-btn">
        <Copy className="w-4 h-4 mr-2" /> Interventi doppi
      </Button>

      {aperto && (
        <Dialog open onOpenChange={() => setAperto(false)}>
          <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Interventi registrati più di una volta</DialogTitle>
            </DialogHeader>

            {carico && <p className="text-sm text-slate-500 py-6 text-center">Controllo in corso...</p>}

            <div className="flex flex-wrap gap-1">
              {CRITERI.map((c) => (
                <button key={c.codice} type="button" disabled={carico}
                        data-testid={`criterio-${c.codice}`}
                        onClick={() => cambiaCriterio(c.codice)}
                        className={`text-xs px-3 py-1.5 rounded-full border ${
                          criterio === c.codice
                            ? "bg-[#0067b2] text-white border-[#0067b2]"
                            : "bg-white text-slate-600 border-slate-200"}`}>
                  {c.nome}
                </button>
              ))}
            </div>

            {!carico && dati && dati.totale_da_togliere === 0 && (
              <p className="text-sm text-emerald-700 flex items-center gap-2 py-6">
                <ShieldCheck className="w-5 h-5" />
                Nessun intervento doppio con questo criterio.
              </p>
            )}

            {!carico && dati && dati.totale_da_togliere > 0 && (
              <div className="space-y-3">
                {criterio === "identiche" ? (
                  <p className="text-sm text-slate-600">
                    Sono raggruppate solo le righe <strong>identiche in tutto</strong>:
                    stessa data, stesso tipo, stessa descrizione, stesso fornitore,
                    stesse note e stesse spunte. Se anche una cosa sola è diversa,
                    qui non compaiono.
                  </p>
                ) : (
                  <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
                    Qui fanno gruppo <strong>stesso mezzo, stessa data e stessa
                    cifra</strong>, anche se il resto è diverso. Le righe non sono
                    uguali: qualcosa si perde. Si propone di tenere quella con
                    più campi compilati — apri le copie e guardale prima di
                    confermare. Le righe senza né importo né tempo restano fuori.
                  </p>
                )}

                <input value={cerca} onChange={(e) => setCerca(e.target.value)}
                       data-testid="cerca-doppioni"
                       placeholder="Cerca targa, marca o descrizione…"
                       className="w-full text-sm border border-slate-200 rounded px-3 py-2" />

                <div className="flex items-center gap-2 text-xs">
                  <button type="button" onClick={() => tutte(true)}
                          className="text-orange-600 hover:underline">spunta tutto</button>
                  <span className="text-slate-300">·</span>
                  <button type="button" onClick={() => tutte(false)}
                          data-testid="deseleziona-tutto"
                          className="text-orange-600 hover:underline">togli tutte le spunte</button>
                  <span className="ml-auto text-slate-500">
                    {selezionate.length} righe scelte su {dati.totale_da_togliere}
                    {cerca && ` · ${visibili.length} gruppi mostrati`}
                  </span>
                </div>

                {visibili.length === 0 && (
                  <p className="text-sm text-slate-500 py-4 text-center">
                    Nessun gruppo per «{cerca}».
                  </p>
                )}

                <div className="border border-slate-200 rounded divide-y divide-slate-100">
                  {visibili.map((g, i) => {
                    const scelteGruppo = g.da_togliere.filter((id) => scelte[id]).length;
                    const apertoOra = aperti[i];
                    return (
                      <div key={i} className="p-3">
                        <div className="flex items-start gap-3">
                          <input type="checkbox" className="mt-1 flex-shrink-0"
                                 data-testid={`gruppo-${i}`}
                                 checked={scelteGruppo === g.da_togliere.length}
                                 ref={(el) => { if (el) el.indeterminate =
                                   scelteGruppo > 0 && scelteGruppo < g.da_togliere.length; }}
                                 onChange={(e) => cambiaGruppo(g, e.target.checked)} />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold truncate">{g.mezzo}</div>
                            <div className="text-xs text-slate-600">
                              {g.data} · {g.tipo} · {g.descrizione}
                            </div>
                            <div className="text-[11px] text-slate-400">
                              {[g.fornitore && `fornitore ${g.fornitore}`,
                                g.prossima && `prossima ${g.prossima}`,
                                g.note && `nota: ${g.note}`].filter(Boolean).join(" · ") || " "}
                            </div>
                            <button type="button" onClick={() => setAperti((a) => ({ ...a, [i]: !a[i] }))}
                                    className="text-[11px] text-orange-600 hover:underline mt-1">
                              {apertoOra ? "nascondi le copie" : `vedi le ${g.copie} copie`}
                            </button>
                            {apertoOra && (
                              <div className="mt-2 space-y-1 bg-slate-50 rounded p-2">
                                {g.righe.map((r, k) => {
                                  const siTiene = r.id === g.tiene;
                                  return (
                                    <label key={r.id}
                                           className={`flex items-start gap-2 text-xs ${
                                             siTiene ? "text-slate-400" : "text-slate-700"}`}>
                                      <input type="checkbox" disabled={siTiene}
                                             className="mt-0.5 flex-shrink-0"
                                             data-testid={`riga-${r.id}`}
                                             checked={siTiene ? false : !!scelte[r.id]}
                                             onChange={(e) => setScelte((s) => ({ ...s, [r.id]: e.target.checked }))} />
                                      <span className="font-mono flex-shrink-0">{k + 1}.</span>
                                      <span className="flex-1 min-w-0">
                                        {criterio === "data_importo" ? (
                                          <>
                                            <span className="block">
                                              {[r.tipo, r.descrizione].filter(Boolean).join(" · ")
                                                || "(senza descrizione)"}
                                            </span>
                                            <span className="block text-[11px] text-slate-400">
                                              {[r.fornitore && `fornitore ${r.fornitore}`,
                                                r.prossima && `prossima ${r.prossima}`,
                                                r.note && `nota: ${r.note}`,
                                                `${r.campi_pieni} campi compilati`,
                                              ].filter(Boolean).join(" · ")}
                                            </span>
                                          </>
                                        ) : (
                                          <span className="block truncate">
                                            scritta il {r.scritta_il?.replace("T", " ") || "—"}
                                            {r.scritta_da ? ` da ${r.scritta_da}` : ""}
                                          </span>
                                        )}
                                      </span>
                                      {siTiene && (
                                        <span className="flex-shrink-0 font-semibold text-emerald-600">
                                          si tiene
                                        </span>
                                      )}
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 flex-shrink-0">×{g.copie}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setAperto(false)}>Chiudi</Button>
              {dati && dati.totale_da_togliere > 0 && (
                <Button onClick={pulisci} disabled={pulendo || selezionate.length === 0}
                        data-testid="pulisci-doppioni-btn"
                        className="bg-red-600 hover:bg-red-700 text-white">
                  {pulendo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                           : <Trash2 className="w-4 h-4 mr-2" />}
                  {selezionate.length === 0
                    ? "Nessuna riga scelta"
                    : `Togli le ${selezionate.length} righe spuntate`}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
